import { AlertTriangle, FileText, Loader2, Search, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CATEGORY_FILTER_OPTIONS,
  FAILURE_TYPE_FILTER_OPTIONS,
} from "@/lib/invoicePageConfig";
import { findOverviewStat } from "@/lib/invoiceOverviewStats";
import { getInsightCount } from "@/lib/overviewInsights";
import { cn } from "@/lib/utils";

/** The strip's count for a chip, if it has one — shown only where the strip is hidden. */
function statCount(insights, field, value) {
  const stat = findOverviewStat(field, value);
  return stat ? (getInsightCount(insights, stat.source) ?? null) : null;
}

/**
 * The option's colour reaches CSS as a custom property rather than as computed
 * `backgroundColor`/`borderColor` values. The previous version built those by
 * concatenating hex alpha onto the colour string (`${option.color}14`), which
 * silently produced garbage for any option whose colour was not a 6-digit hex —
 * the same bug the invoice labels had.
 */
function chipColorStyle(color) {
  return color ? { "--chip-color": color } : undefined;
}

export function InvoiceTableToolbar({
  config,
  searchValue,
  onSearchChange,
  categoryFilter,
  failureTypeFilter,
  onCategoryFilterChange,
  onFailureTypeFilterChange,
  onFilterClick,
  filterCount = 0,
  filterOpen = false,
  isSearching = false,
  insights = null,
}) {
  // Every queue that filters by button offers the same two groups, Category and
  // Failure type; the status is filtered from the stat cards and the filter panel.
  const showFilterGroups = config.showColoredFilters || config.showQueueChips !== false;

  return (
    <div className="page-toolbar shrink-0">
      <div className="flex flex-col gap-3 p-2.5 lg:flex-row lg:flex-wrap lg:items-end">
        {showFilterGroups && (
          <div className="queue-filter-inline">
            <FilterChipGroup
              label="Category"
              icon={FileText}
              options={CATEGORY_FILTER_OPTIONS}
              value={categoryFilter}
              onChange={onCategoryFilterChange}
              getCount={(option) => statCount(insights, "category", option.value)}
            />
            <FilterChipGroup
              label="Failure type"
              icon={AlertTriangle}
              options={FAILURE_TYPE_FILTER_OPTIONS}
              value={failureTypeFilter}
              onChange={onFailureTypeFilterChange}
              getCount={(option) => statCount(insights, "failureType", option.value)}
            />
          </div>
        )}

        <div className={cn("flex shrink-0 items-center gap-4 lg:ml-auto", !showFilterGroups && "flex-1 justify-end")}>
          <div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
            {isSearching ? (
              <Loader2
                className="pointer-events-none absolute top-1/2 inset-s-3 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
            ) : (
              <Search
                className="pointer-events-none absolute top-1/2 inset-s-3 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
            )}
            <Input
              type="search"
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={config.searchPlaceholder}
              aria-label={config.searchPlaceholder}
              maxLength={80}
              className="h-11 rounded-[0.8rem] border-line bg-card ps-9 text-base shadow-none md:h-9 md:text-sm"
            />
          </div>
          {config.showFilterButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onFilterClick}
              aria-haspopup="dialog"
              aria-expanded={filterOpen}
              className={cn(
                "h-11 shrink-0 rounded-[0.8rem] border-line px-3 shadow-none md:h-9",
                filterCount > 0 && "border-primary bg-primary text-primary-foreground",
              )}
            >
              <SlidersHorizontal data-icon="inline-start" />
              Filter
              {filterCount > 0 && (
                <span className="num ms-1 text-xs tabular-nums">({filterCount})</span>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One filter dimension as a segmented control, the same idiom the queue chips
 * beside it use. Both groups used to share a single wrapping row in a stripe of
 * their own below the toolbar, with nothing marking where "Category" ended and
 * "Failure type" began.
 */
function FilterChipGroup({ label, icon: Icon, options, value, onChange, getCount }) {
  return (
    <fieldset className="queue-filter-row">
      <legend className="queue-filter-legend">
        {Icon && <Icon className="queue-filter-legend-icon" aria-hidden="true" />}
        {label}
      </legend>
      <div className="queue-chip-bar queue-filter-bar">
        <button
          type="button"
          onClick={() => onChange("")}
          aria-pressed={value === ""}
          className={cn("queue-filter-chip touch-target-chip", value === "" && "is-active")}
        >
          All
        </button>
        {options.map((option) => {
          const isActive = value === option.value;
          const count = getCount?.(option);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={isActive}
              style={chipColorStyle(option.color)}
              className={cn("queue-filter-chip touch-target-chip", isActive && "is-active")}
            >
              <span className="filter-chip-swatch" aria-hidden="true" />
              {option.label}
              {count != null && <span className="queue-chip-count num">{count}</span>}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
